import {useCallback, useMemo, useRef} from 'react';
import isEqual from 'react-fast-compare';

import {runPreviewAnalysisStep} from 'analysis-tool/AnalysisActions';
import {assert} from 'Asserts';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PreviewOutput, PreviewStepParams} from 'pipelines/network/PipelineDAO';
import {PipelineStep} from 'pipelines/PipelineTypes';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import {AsyncState} from 'ui-library/hooks/useAsyncState';

import useDraftSteps, {DraftableStep, DraftStepFields, UseDraftStepOutput} from './useDraftSteps';

const previewAsyncGlobalState = makeUseGlobalClientState<AsyncState<PreviewOutput<PipelineStepType>> | null>(
  null
);

export interface UseDraftStepWithPreviewOutput extends UseDraftStepOutput {
  areStepsPreviews: {[stepId: string]: boolean};
  generatePreviewForStep: (stepId: string) => void;
  stepPreviews: {[stepId: string]: AsyncState<PreviewOutput<PipelineStepType>> | null};
}

/**
 * Hook for managing draft step state with automatic preview generation.
 *
 * This hook extends `useDraftSteps` by adding automatic preview generation for draft steps.
 * Preview state is stored in global client state so it's shareable across components.
 *
 * Preview Generation Logic:
 * - Previews are only generated for steps that have a draft state (i.e., `updateDraftStep` has been called).
 * - When `updateDraftStep` is called, it first stores the new draft state and then it immediately triggers preview
 * generation.
 *
 * **Important:** This hook has no side effects. All preview requests are made during its function calls. If you want to trigger
 * a preview manually as a side effect (e.g. on mount), you can use `generatePreviewForStep` to do so from the
 * caller after setting.
 *
 * @param steps - Array of `DraftableStep`s to manage draft state.  Draft steps only need `id`, `stepType`,
 * `stepVersion`, `config`, and `inputStepDatas` to work with this hook. Uncreated steps need to have a fake client-only ID.
 * @returns `areStepsDrafts` - A map of step IDs to whether the step is a draft.
 * @returns `areStepsPreviews` - A map of step IDs to whether the step has a preview.
 * @returns `clearDraft` - A function to clear the draft state for a step.
 * @returns `draftSteps` - A map of step IDs to their draft state (or original state if no draft state is set yet).
 * @returns `generatePreviewForStep` - A function to manually trigger preview generation for a step using its current
 * draft state. Useful for implementing preview side effects like previewing on mount or for implementing refreshing.
 * @returns `stepPreviews` - A map of step IDs to their preview state (or `null` if no preview has been generated yet).
 * @returns `updateDraftStep` - A function to update the draft state for a step.
 */
export default function useDraftStepsWithPreview(steps: Array<DraftableStep>): UseDraftStepWithPreviewOutput {
  const draftStepsOutput = useDraftSteps(steps);
  const stepIds = steps.map((step) => step.id);
  const [previewStates, setPreviewStates] = previewAsyncGlobalState.useGlobalClientStates(stepIds);

  // We use a ref to track the preview parameters that were used when each preview request was initiated.
  // This is to prevent stale preview results from overwriting newer preview data when multiple
  // updates happen in quick succession. Subsequent updates will update the ref and make this particular
  // closure skip the update because it's params no longer match the ref.
  const pendingPreviewParamsRef = useRef<{[stepId: string]: PreviewStepParams<PipelineStepType> | null}>({});

  const internalGeneratePreviewForStep = useCallback(
    (stepId: string, previewParams: PreviewStepParams<PipelineStepType> | null): void => {
      pendingPreviewParamsRef.current[stepId] = previewParams;

      if (previewParams != null) {
        runPreviewAnalysisStep(
          previewParams,
          {status: AsyncStatus.NOT_FETCHED},
          (nextState: AsyncState<PreviewOutput<PipelineStepType>>): void => {
            if (isEqual(pendingPreviewParamsRef.current[stepId], previewParams)) {
              setPreviewStates(stepId, nextState);
            }
          }
        );
      }
    },
    [setPreviewStates]
  );

  const areStepsPreviews = useMemo(
    () => Object.fromEntries(stepIds.map((stepId) => [stepId, previewStates[stepId] != null])),
    [previewStates, stepIds]
  );

  // Override `clearDraft` to also clear preview state
  const clearDraft = useCallback(
    (stepId: string): void => {
      draftStepsOutput.clearDraft(stepId);
      previewAsyncGlobalState.deleteState(stepId);
      delete pendingPreviewParamsRef.current[stepId];
    },
    [draftStepsOutput]
  );

  const generatePreviewForStep = useCallback(
    (stepId: string): void => {
      const draftStep = draftStepsOutput.draftSteps[stepId];
      const previewParams = getPreviewStepParams(draftStep, {});
      internalGeneratePreviewForStep(stepId, previewParams);
    },
    [draftStepsOutput.draftSteps, internalGeneratePreviewForStep]
  );

  const stepPreviews = useMemo(
    () => Object.fromEntries(stepIds.map((stepId) => [stepId, previewStates[stepId]])),
    [previewStates, stepIds]
  );

  // Override `updateDraftStep` to trigger preview generation after updating draft
  const updateDraftStep = useCallback(
    (stepId: string, draftStepFields: Partial<DraftStepFields>): void => {
      const originalStep = steps.find((step) => step.id === stepId);
      assert(originalStep != null, 'Expected step id to be from a step specified in the input');
      const previewParams = getPreviewStepParams(originalStep, draftStepFields);

      draftStepsOutput.updateDraftStep(stepId, draftStepFields);
      internalGeneratePreviewForStep(stepId, previewParams);
    },
    [draftStepsOutput, internalGeneratePreviewForStep, steps]
  );

  return {
    ...draftStepsOutput,
    areStepsPreviews,
    clearDraft,
    generatePreviewForStep,
    stepPreviews,
    updateDraftStep,
  };
}

function getPreviewStepParams(
  originalStep: DraftableStep,
  draftFields: Partial<Pick<PipelineStep, 'config' | 'stepType' | 'stepVersion'>>
): PreviewStepParams<PipelineStepType> | null {
  const draftStep: DraftableStep = {
    ...originalStep,
    ...draftFields,
  };

  if (draftStep.config == null) {
    return null;
  }
  const config = draftStep.config;
  const inputStepDatas = draftStep.inputStepDatas ?? [];

  const stepInput = Object.fromEntries(
    inputStepDatas.map((inputStepData) => [inputStepData.name, inputStepData.rawDatas.map(({id}) => id)])
  );

  return {
    stepType: draftStep.stepType,
    stepConfig: config,
    stepInput,
  };
}
